import { REACTION_ALLOWLIST, SIGNAL_LIMITS } from '../protocol/constants.js';
import { utf8Decode, utf8Encode } from '../util/encoding.js';
import { TypedEmitter } from '../util/emitter.js';
import { systemClock } from '../protocol/types.js';
const isReaction = (value) => typeof value === 'string' && REACTION_ALLOWLIST.includes(value);
export const encodeSignal = (message) => {
    const bytes = utf8Encode(JSON.stringify(message));
    if (bytes.length > SIGNAL_LIMITS.maxBytes) {
        throw new Error('Signal exceeds size limit');
    }
    return bytes;
};
export const decodeSignal = (payload) => {
    if (payload.length === 0 || payload.length > SIGNAL_LIMITS.maxBytes) {
        return undefined;
    }
    let parsed;
    try {
        parsed = JSON.parse(utf8Decode(payload));
    }
    catch {
        return undefined;
    }
    if (!parsed || typeof parsed !== 'object') {
        return undefined;
    }
    const candidate = parsed;
    if (candidate.v !== 1 || typeof candidate.ts !== 'number' || typeof candidate.n !== 'string' || candidate.n.length > 64) {
        return undefined;
    }
    if (candidate.t === 'reaction' && isReaction(candidate.e)) {
        return { v: 1, t: 'reaction', e: candidate.e, ts: candidate.ts, n: candidate.n };
    }
    if (candidate.t === 'hand' && typeof candidate.up === 'boolean') {
        return { v: 1, t: 'hand', up: candidate.up, ts: candidate.ts, n: candidate.n };
    }
    return undefined;
};
const randomNonce = () => Math.random().toString(36).slice(2, 12);
export class SignalChannel {
    constructor(isKnownSender, clock = systemClock) {
        this.isKnownSender = isKnownSender;
        this.clock = clock;
        this.emitter = new TypedEmitter();
        this.raised = new Set();
        this.sentAt = [];
        this.seenNonces = new Set();
        this.ownHandUp = false;
    }
    attachTransport(transport) {
        this.transport = transport;
    }
    get handRaised() {
        return this.ownHandUp;
    }
    raisedHands() {
        return new Set(this.raised);
    }
    async sendReaction(reaction) {
        if (!isReaction(reaction)) {
            return false;
        }
        return this.send({ v: 1, t: 'reaction', e: reaction, ts: this.clock.now(), n: randomNonce() });
    }
    async setHand(up, localIdentity) {
        this.ownHandUp = up;
        this.updateHand(localIdentity, up);
        return this.send({ v: 1, t: 'hand', up, ts: this.clock.now(), n: randomNonce() }, true);
    }
    async rebroadcastHand() {
        if (this.ownHandUp) {
            await this.send({ v: 1, t: 'hand', up: true, ts: this.clock.now(), n: randomNonce() }, true);
        }
    }
    receive(payload, fromIdentity) {
        if (!fromIdentity || !this.isKnownSender(fromIdentity)) {
            return;
        }
        const message = decodeSignal(payload);
        if (!message) {
            return;
        }
        const nonceKey = `${fromIdentity}|${message.n}`;
        if (this.seenNonces.has(nonceKey)) {
            return;
        }
        this.seenNonces.add(nonceKey);
        if (this.seenNonces.size > 500) {
            const oldest = this.seenNonces.values().next().value;
            if (oldest !== undefined) {
                this.seenNonces.delete(oldest);
            }
        }
        if (message.t === 'reaction') {
            this.emitter.emit('reaction', { from: fromIdentity, reaction: message.e, at: this.clock.now(), id: nonceKey });
        }
        else {
            this.updateHand(fromIdentity, message.up);
        }
    }
    participantLeft(identity) {
        if (this.raised.delete(identity)) {
            this.emitter.emit('hands', this.raisedHands());
        }
    }
    on(event, listener) {
        return this.emitter.on(event, listener);
    }
    dispose() {
        this.emitter.removeAll();
        this.raised.clear();
        this.seenNonces.clear();
        this.transport = undefined;
    }
    updateHand(identity, up) {
        const changed = up ? !this.raised.has(identity) : this.raised.has(identity);
        if (up) {
            this.raised.add(identity);
        }
        else {
            this.raised.delete(identity);
        }
        if (changed) {
            this.emitter.emit('hands', this.raisedHands());
        }
    }
    async send(message, bypassThrottle = false) {
        const transport = this.transport;
        if (!transport) {
            return false;
        }
        const now = this.clock.now();
        while (this.sentAt.length > 0 && now - this.sentAt[0] > SIGNAL_LIMITS.throttleWindowMs) {
            this.sentAt.shift();
        }
        if (!bypassThrottle && this.sentAt.length >= SIGNAL_LIMITS.throttleCount) {
            return false;
        }
        this.sentAt.push(now);
        await transport.publish(encodeSignal(message));
        return true;
    }
}
